import React, { useEffect, useMemo, useState } from "react";
import { Bell, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { appAlert } from "../../../utils/appPopup";
import { ensureWarehouseContext } from "../../../services/warehouseContext.service";
import { getAuthSession, getAuthUser } from "../../AdminAuth/components/authStore";
import {
  NotificationsService,
  mapNotificationToRoute,
  isWarehousePanelNotification,
} from "../../../services/notifications.service";
import { connectSocket } from "../../../services/notificationsSocket";

function timeAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
}

function getTitle(item) {
  return item?.title || item?.event_type || item?.type || "Notification";
}

function getMessage(item) {
  return item?.message || item?.body || item?.description || "You have a new notification.";
}

function getNotificationWarehouseId(item = {}) {
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return String(
    item?.warehouseId ||
      item?.warehouse_id ||
      meta?.warehouseId ||
      meta?.warehouse_id ||
      meta?.selectedWarehouseId ||
      meta?.assignedWarehouseId ||
      ""
  ).trim();
}

function canOpenWarehousePanel(item = {}) {
  const session = getAuthSession() || {};
  const user = getAuthUser() || {};
  const dashboardAccess = session?.dashboardAccess || {};
  const warehouseAccess = session?.warehouseAccess || {};
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const role = String(user?.role || "").toLowerCase();

  const hasWarehousePanelAccess =
    role === "super_admin" ||
    !!dashboardAccess?.canOpenWarehouseDashboard ||
    !!user?.canAccessWarehouseDashboard ||
    !!user?.canSwitchToWarehouseDashboard ||
    permissions.includes("warehouse.panel.access") ||
    permissions.includes("wh.panel.access");

  if (!hasWarehousePanelAccess) return false;

  const notificationWarehouseId = getNotificationWarehouseId(item);
  if (!notificationWarehouseId) return true;

  if (
    role === "super_admin" ||
    warehouseAccess?.mode === "all" ||
    !!warehouseAccess?.canSwitchAnyWarehouse
  ) {
    return true;
  }

  const allowedWarehouseIds = [
    ...(Array.isArray(warehouseAccess?.warehouseIds) ? warehouseAccess.warehouseIds : []),
    warehouseAccess?.assignedWarehouseId,
    session?.selectedWarehouseId,
  ]
    .filter(Boolean)
    .map((id) => String(id));

  return allowedWarehouseIds.includes(notificationWarehouseId);
}

function showWarehousePermissionAlert() {
  appAlert("You do not have permission to access the warehouse panel.", {
    title: "Permission denied",
    danger: true,
    confirmText: "OK",
  });
}

async function allowNotificationRedirect(item, targetRoute) {
  if (!isWarehousePanelNotification(item, targetRoute)) return true;

  if (!canOpenWarehousePanel(item)) {
    showWarehousePermissionAlert();
    return false;
  }

  const notificationWarehouseId = getNotificationWarehouseId(item);
  try {
    await ensureWarehouseContext(notificationWarehouseId);
    return true;
  } catch (err) {
    console.error("Failed to establish warehouse context:", err);
    appAlert(
      err?.message || "Unable to open the warehouse right now. Please try again.",
      {
        title: "Warehouse switch failed",
        danger: true,
        confirmText: "OK",
      }
    );
    return false;
  }
}

export default function NotificationsPage() {
  const user = useMemo(() => getAuthUser() || {}, []);
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(1);

  const limit = 20;

  const unreadCount = items.filter((n) => !(n?.is_read || n?.isRead)).length;

  async function loadNotifications(currentPage = page) {
    try {
      setLoading(true);

      const res = await NotificationsService.list(user, {
        limit,
        offset: (currentPage - 1) * limit,
      });

      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleNotificationClick(item) {
    const targetRoute = mapNotificationToRoute(item);

    if (!(await allowNotificationRedirect(item, targetRoute))) {
      return;
    }

    try {
      if (item?.id && !(item?.is_read || item?.isRead)) {
        await NotificationsService.markRead(item.id, user);
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    } finally {
      navigate(targetRoute);
    }
  }

  async function handleMarkRead(id) {
    try {
      await NotificationsService.markRead(id, user);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, is_read: true, isRead: true } : item
        )
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }

  async function handleMarkAllRead() {
    try {
      setMarkingAll(true);
      await NotificationsService.markAllRead(user);
      setItems((prev) =>
        prev.map((item) => ({ ...item, is_read: true, isRead: true }))
      );
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    } finally {
      setMarkingAll(false);
    }
  }

  useEffect(() => {
    loadNotifications(page);
  }, [page]);

  useEffect(() => {
    const socket = connectSocket();

    if (!socket) return undefined;

    const handleNotification = (data) => {
      setItems((prev) => [data, ...prev]);
    };

    socket.on("notification", handleNotification);

    return () => {
      socket.off("notification", handleNotification);
    };
  }, []);

  return (
    <div className="min-h-screen  p-6">
      <div className="mx-auto max-w-8xl">
        <div className="mb-6 flex flex-col gap-4 rounded-[24px] border border-[#f2c6cc] bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
              <Bell size={22} />
            </div>

            <div>
              <h1 className="text-xl font-bold text-slate-800">All Notifications</h1>
              <p className="text-sm text-slate-500">
                {unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
                  : "You are all caught up"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => loadNotifications(page)}
              className="inline-flex items-center gap-2 rounded-full border border-[#f2c6cc] bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#d86a78]"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={markingAll || unreadCount === 0}
              className="rounded-full bg-[#d86a78] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {markingAll ? "Reading..." : "Read all"}
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-[24px] border border-[#f2c6cc] bg-white shadow-sm">
          {loading ? (
            <div className="p-6 text-sm text-slate-400">Loading notifications...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              No notifications found
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {items.map((item) => {
                const unread = !(item?.is_read || item?.isRead);

                return (
                  <div
                    key={item?.id}
                    className="flex items-start justify-between gap-4 px-5 py-4 transition hover:bg-[#fff8f9]"
                  >
                    <button
                      type="button"
                      onClick={() => handleNotificationClick(item)}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-slate-800">
                          {getTitle(item)}
                        </span>
                        {unread ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-[#d86a78]" />
                        ) : null}
                      </div>

                   <div className="mt-1 text-[13px] leading-5 text-slate-500">
  {item?.meta?.actorName ? (
    <>
      <div className="font-semibold text-slate-700">{item.meta.actorName}</div>
      {item?.meta?.actorEmail ? (
        <div className="text-[12px] text-slate-400">{item.meta.actorEmail}</div>
      ) : null}
      <div className="mt-1">
        {item?.meta?.displayAction || getMessage(item)}
      </div>
    </>
  ) : (
    <div className="whitespace-pre-line">{getMessage(item)}</div>
  )}
</div>

                      <div className="mt-2 text-xs text-slate-400">
                        {timeAgo(item?.created_at || item?.createdAt)}
                      </div>
                    </button>

                    {unread ? (
                      <button
                        type="button"
                        onClick={() => handleMarkRead(item.id)}
                        className="shrink-0 rounded-full border border-[#f2c6cc] px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-[#d86a78]"
                      >
                        Mark read
                      </button>
                    ) : (
                      <span className="shrink-0 text-xs font-medium text-emerald-600">
                        Read
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="rounded-full border border-[#f2c6cc] bg-white px-4 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>

          <span className="text-sm font-semibold text-slate-600">
            Page {page}
          </span>

          <button
            type="button"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={items.length < limit}
            className="rounded-full border border-[#f2c6cc] bg-white px-4 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}